import { AtlassianProductHostingType } from '@atlassian/mpac-types/graphql-gateway-types';
import * as Types from '@atlassian/mpac-types/graphql-gateway-types';
import * as editionConstants from './editionConstants';
import { DEFAULT_SELECTED_PLAN, DEFAULT_SELECTED_PLAN_NO_ADVANCED } from './editionConstants';
const {
  Free,
  Standard,
  Advanced
} = Types.MarketplaceStoreEditionType;
const {
  FEATURES_TITLE_FOR_FREE_EDITION_VIEW,
  FEATURES_TITLE,
  EDITIONS
} = editionConstants;
const {
  DESCRIPTION_WITH_FREE,
  DESCRIPTION_WITHOUT_ADVANCED,
  DESCRIPTION
} = EDITIONS;

/**
 * appSupportsEditions returns 'true' when:
 *    1. atleast 2 editions are present, and
 *    2. billingSystem is either 'CCP' or undefined ('HAMS' sites are not allowed)
 *    3. if hostingType is provided, it must be Cloud
 *
 *    NOTE: the above 'undefined' usecase is for MPAC, where billingSystem is not known
 */
export const appSupportsEditions = _ref => {
  let {
    editions,
    billingSystem,
    hostingType
  } = _ref;
  const hasMultipleEditions = !!editions && editions.length > 1 && billingSystem !== 'HAMS';
  if (hostingType) {
    return hasMultipleEditions && hostingType === AtlassianProductHostingType.Cloud;
  }
  return hasMultipleEditions;
};

/**
 * hasEditionType returns 'true' when the specified edition type is present
 * Accepts either AppEditionDetails[] or MarketplaceStoreEditionType[]
 */
const hasEditionType = (editionType, input) => {
  if (!input || input.length === 0) {
    return false;
  }
  const isAppEditionDetails = item => typeof item === 'object' && item !== null && 'type' in item;
  const editionTypes = isAppEditionDetails(input[0]) ? input.map(_ref2 => {
    let {
      type
    } = _ref2;
    return type;
  }) : input;
  return editionTypes.some(type => type === editionType);
};

/**
 * appSupportsFreeEdition returns 'true' when a free edition is present
 */
export const appSupportsFreeEdition = input => {
  return hasEditionType(Free, input);
};

/**
 * appSupportsAdvancedEdition returns 'true' when an advanced edition is present
 */
export const appSupportsAdvancedEdition = input => {
  return hasEditionType(Advanced, input);
};

/**
 * getDefaultSelectedPlan returns the default selected plan based on the edition types
 */
export const getDefaultSelectedPlan = editionTypes => {
  return appSupportsAdvancedEdition(editionTypes) ? DEFAULT_SELECTED_PLAN : DEFAULT_SELECTED_PLAN_NO_ADVANCED;
};

/**
 * getEditionDescription returns the description for the edition types, based on what editions are supported
 * and if editions M4 feature flag is enabled
 */
export const getEditionDescription = (editionTypes, enableFreeAppEditionsView) => {
  if (enableFreeAppEditionsView && appSupportsFreeEdition(editionTypes)) {
    return appSupportsAdvancedEdition(editionTypes) ? DESCRIPTION_WITH_FREE : DESCRIPTION_WITHOUT_ADVANCED;
  }
  return DESCRIPTION;
};
export const getFeaturesTitle = _ref3 => {
  let {
    currentEdition,
    allEditions,
    isFreeEditionViewEnabled
  } = _ref3;
  const featuresTitle = isFreeEditionViewEnabled ? FEATURES_TITLE_FOR_FREE_EDITION_VIEW : FEATURES_TITLE;
  // if app supports free edition, title for standard edition should be "Everything from Free, plus:"
  // if app does not support free edition, title for standard edition should be "Includes:"
  if (!appSupportsFreeEdition(allEditions) && currentEdition.type === Standard) {
    return featuresTitle[Free];
  }
  return featuresTitle[currentEdition.type];
};